<script lang="ts">
  import { styleToString } from "@shared/common/node/style";
  import type { IPartialDocumentData } from "@shared/common/node/type";
  import type {
    Data,
    IDataMap,
    IVideoData,
  } from "@shared/common/types/data-source";
  import type {
    OnFormStateChangeArgs,
    OnFormSubmitArgs,
    OnFormUploadArgs,
  } from "@shared/common/types/form";
  import type {
    AnalyticsCampaignType,
    AnalyticsEvent,
    IAnalyticsBootResponse,
    IReactionEvent,
    NodeEventData,
  } from "@shared/common/types/sdk";
  // 직접 import 수행. ModuleFederationPlugin 를 통하지 않음.
  import { getOS } from "@shared/common/component-manager/util";
  import { traverseTarget } from "@shared/common/data/campaign-feature/target";
  import { checkSchedule } from "@shared/common/domain/schedule";
  import { findEnagageableCampaign } from "@shared/common/target-segment";
  import type {
    ICampaignListJson,
    ICampaignSetting,
  } from "@shared/common/types/campaign";
  import { EmbeddedApp } from "codenbutter-component";
  import type { Properties as CSSProperties } from "csstype";
  import { getContext } from "svelte";
  import { createAnalyticsManager } from "../analytics-manager";
  import failImage from "../assets/fail.png";
  import successImage from "../assets/success.png";
  import { createInitialUserData, getDataMapItems } from "../data";
  import { createDeduplicationSet } from "../deduplication-set";
  import { locale, t } from "../i18n/index";
  import { getCampaignListJson, getDocumentJson, getVideoJson } from "../json";
  import currentUrl from "../page/current-url";
  import { fetchSubmission } from "../page/fetch-submission";
  import { createSegmentManager } from "../segment-manager";
  import { createDefaultVisitorDataLoader } from "../sites/visitor-data-loader";
  import { State } from "../state";
  import {
    generatePresignedUrl,
    uploadedFilePath,
    uploadFileByPresignedUrl,
  } from "../upload";
  import Loader from "./loader.svelte";
  import Overlay from "./overlay.svelte";

  export let analyticsCampaignType: AnalyticsCampaignType;
  export let successTitle: string;
  export let successDescription: string;
  export let failTitle: string;
  export let failDescription: string;
  export let maxWidth = { value: 700, unit: "px" };
  export let wideMarginTop = "50px";

  let submissionState: "idle" | "uploading" | "submitting" = "idle";
  let submissionError: unknown = null;
  let loading: boolean = true;
  let page: "main" | "success" | "fail" = "main";
  let failType: "not_scheduled" | "not_targeted" | null = null;

  const campaignSetting = getContext<ICampaignSetting>("campaignSetting");
  const siteId = $currentUrl.searchParams.get("s");
  const isDebug = $currentUrl.searchParams.get("cb_debug") === "true";
  const visitorDataLoaderState = new State(createDefaultVisitorDataLoader());

  $: {
    locale.set(campaignSetting.language || "ko");
  }

  if (!siteId) {
    // eslint-disable-next-line no-console
    console.error("siteId(s) is required");
    page = "fail";
  }

  const campaignId = $currentUrl.searchParams.get("c");
  if (!campaignId) {
    // eslint-disable-next-line no-console
    console.error("campaignId(c) is required");
    page = "fail";
  }

  const campaignListJsonState = new State<ICampaignListJson>();
  if (siteId) {
    campaignListJsonState.set(getCampaignListJson({ siteId }));
  }

  let documentData: IPartialDocumentData | null = null;
  let dataMap: IDataMap = {};

  if (siteId && campaignId) {
    getDocumentJson({ siteId, campaignId })
      .then((res) => {
        documentData = res;
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      });
  }

  const getDeviceFromWindow = () => {
    if (getOS() !== "Desktop") {
      return "MOBILE";
    }

    const { innerWidth } = window;
    if (innerWidth > 100 && innerWidth < 430) {
      return "MOBILE";
    }

    return "DESKTOP";
  };

  $: documentNode =
    documentData?.version === "0.0.1"
      ? documentData?.documents[0]
      : documentData?.documents?.find(
          (document) => document.device === getDeviceFromWindow()
        );

  $: {
    // document node 신규 프로퍼티 없을 경우 채워넣기
    if (documentNode) {
      if (!documentNode.maxWidth) {
        documentNode.maxWidth = maxWidth;
      }

      if (!documentNode.alignHorizontal) {
        documentNode.alignHorizontal = "CENTER";
      }
    }
  }

  $: wrapperStyle = (() => {
    const style: CSSProperties = {};

    Object.assign(style, {
      "--cb-wide-wrapper-margin-top": wideMarginTop,
    });

    return styleToString(style);
  })();

  if (isDebug) {
    import("../debug")
      .then((m) => {
        return m.default({
          visitorDataLoaderState,
          campaignListJsonState,
          siteId,
          campaignId,
          customData: Promise.resolve({}),
          user: createInitialUserData(),
          bootResState: new State<IAnalyticsBootResponse>({
            goals: [],
            isNewUser: false,
            location: {
              city: "",
              country: "",
              ip: "",
              postal: "",
              region: "",
            },
            organizationStatus: 1,
            easyLoginStatus: 1,
            disableListener: true,
            features: {},
            utm: {},
          }),
          customDataState: {
            getInitialState: () => ({}),
            getState: () => ({}),
            setState() {},
            subscribe: () => () => {},
            destroy: () => {},
          },
        });
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error(e);
      });
  }

  // DataMap 세팅
  (async () => {
    const campaignListJson = await campaignListJsonState.get();
    const campaigns = campaignListJson.campaigns;
    const campaign = campaigns.find((c) => c.publicId === campaignId);
    const dataSources = campaign?.dataSources;

    if (!campaign) {
      throw new Error("campaign not found");
    }

    const dataMapItems = await getDataMapItems(
      campaign,
      visitorDataLoaderState,
      siteId ?? ""
    );
    dataMapItems.forEach((item) => {
      dataMap[item.alias] = {
        name: item.alias,
        data: item.json,
        id: item.id,
      } as Data;
    });

    if (dataSources && dataSources.length > 0) {
      // 비디오 데이터 로드
      const videoDataMap: IVideoData = {
        id: campaignId ?? "",
        name: "video",
        data: [],
      };

      await Promise.all(
        dataSources.map(async ({ sourceParam, sourceType }) => {
          if (!sourceType) {
            return;
          }

          switch (sourceType) {
            case "CODENBUTTER_VIDEO": {
              if (sourceParam && sourceParam.data.length > 0) {
                const dataJson = await Promise.all(
                  sourceParam.data.map(async (data) => {
                    const json = await getVideoJson({
                      filename: data.filename,
                    });
                    json.filename = data.filename;
                    return json;
                  })
                );

                videoDataMap.data = [...videoDataMap.data, ...dataJson];
              }
              break;
            }

            case "CODENBUTTER_PRODUCTS":
            case "CODENBUTTER_INSTAGRAM":
            case "HTTP":
            case "CODENBUTTER_CAMPAIGN_LIST":
            case "CODENBUTTER_CHILD_LAYOUTS":
              break;
          }
          return;
        })
      );

      dataMap.video = videoDataMap;
    }
  })().catch((e) => {
    // eslint-disable-next-line no-console
    console.error(e);
  });

  const eventDeduplicationSet = createDeduplicationSet();

  // Init User Data 설정 시, 무한 팬딩 방지를 위한 초기화
  const user = createInitialUserData();
  user.isLogin.set(false);
  user.loginId.set("");
  user.cart.set(null);
  user.wishlistCount.set(0);
  user.coupon.set(null);
  user.engageableCampaigns.set([]);

  const analyticsManager = createAnalyticsManager({
    user,
    visitorDataLoaderState,
    eventDeduplicationSet,
  });
  const segementManager = createSegmentManager();

  user.loginId.subscribe((loginIdState) => {
    if (loginIdState.type !== "ready") {
      return;
    }
    user.engageableCampaigns.set(
      segementManager.getEngageableCampaigns(loginIdState.data)
    );
  });

  (async () => {
    const campaignListJson = await campaignListJsonState.get();
    const campaigns = campaignListJson.campaigns;
    const campaign = campaigns.find((c) => c.publicId === campaignId);

    if (!campaign) {
      throw new Error("campaign not found");
    }

    const checkScheduleResult = checkSchedule({
      currentDate: new Date(),
      currentDevice: "ALL",
      schedule: campaign.schedule,
    });

    if (checkScheduleResult.type === "FAIL") {
      page = "fail";
    }

    const engageableCampaigns = await user.engageableCampaigns.get();
    traverseTarget(campaign.target, async (target) => {
      if (target.type === "USERS") {
        const isUserEngageable = findEnagageableCampaign(
          engageableCampaigns,
          campaign.publicId
        );
        if (!isUserEngageable) {
          page = "fail";
          failType = "not_targeted";
        }
      }
    });

    // 성공시 타이틀 설정
    if (campaign.meta?.title) {
      document.title = campaign.meta.title;
    }
  })()
    .catch((e) => {
      page = "fail";

      // eslint-disable-next-line no-console
      console.error(e);
    })
    .finally(() => {
      loading = false;
    });

  const hash = $currentUrl.hash.split("#")[1];
  if (hash) {
    dataMap.user = { name: "user", id: "user", data: {} };
    const userDataMap = dataMap.user;
    const hashParams = new URLSearchParams(hash);
    hashParams.forEach((value, key) => {
      if (user) {
        userDataMap.data[key] = value;
      }

      if (key === "id") {
        user.loginId.set(value);
      } else if (key === "name") {
        user.name.set(value);
      } else if (key === "email") {
        user.email.set(value);
      }
    });
    history.replaceState({}, document.title, $currentUrl.href.split("#")[0]);
  }

  async function onImpressionEvent() {
    if (!campaignId || !siteId) {
      return;
    }

    const insightData: AnalyticsEvent = {
      type: "IMPRESSION",
      campaignType: analyticsCampaignType,
      campaignId,
      siteId,
    };

    await analyticsManager.sendAnalyticsEvent(insightData);
  }

  async function onSendCollectEvent(data: NodeEventData) {
    if (!campaignId || !siteId) {
      return;
    }

    const analyticsEvent: IReactionEvent = {
      type: "REACTION",
      campaignType: analyticsCampaignType,
      data,
      campaignId,
      siteId,
    };

    await analyticsManager.sendAnalyticsEvent(analyticsEvent);
  }

  async function onSubmit(args: OnFormSubmitArgs) {
    try {
      if (submissionState === "submitting") {
        // eslint-disable-next-line no-console
        console.warn($t("main.alreadySubmitting"));
        return;
      }
      if (!campaignId || !siteId) {
        return;
      }

      const userId = await user.loginId.get();
      const engageableCampaigns = await user.engageableCampaigns.get();
      const engageableCampaign = findEnagageableCampaign(
        engageableCampaigns,
        campaignId
      );

      await onStateChange({ state: "submitting" });

      const res = await fetchSubmission({
        campaignId,
        userId,
        data: args.data,
        properties: engageableCampaign?.properties,
      });

      if (!res.ok) {
        throw new Error("submit failed: " + res.status + " " + res.statusText);
      }

      submissionError = null;
      analyticsManager
        .sendAnalyticsEvent({
          type: "LEAD",
          siteId,
          campaignId,
          campaignType: analyticsCampaignType,
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.error(e);
        });
      page = "success";
      args.element.reset();
    } catch (e) {
      submissionError = e;
      alert($t("main.submitFailed"));
    } finally {
      await onStateChange({ state: "idle" });
    }
  }

  async function onUpload(args: OnFormUploadArgs) {
    try {
      const files = args.files;

      if (!files || files.length === 0) {
        // eslint-disable-next-line no-console
        console.warn("no selected files"); // TODO: i18n 처리 필요
        return [];
      }

      if (!campaignId || !siteId) {
        return [];
      }

      const presignedUrlData = await generatePresignedUrl({
        siteId,
        campaignId,
        filenames: files.map((file) => file.name),
      });

      const uploadFiles = await Promise.all(
        files.map(async (file) => {
          const presignedUrl = presignedUrlData.data.find(
            (data) => data.originalFilename === file.name
          );

          if (!presignedUrl) {
            throw new Error("presigned url not found");
          }

          const res = await uploadFileByPresignedUrl({
            file,
            presignedUrl: presignedUrl.presignedUrl,
          });

          if (res.status === "fail") {
            throw new Error("upload failed: " + res.message);
          }

          const url = uploadedFilePath({
            siteId,
            campaignId,
            filename: presignedUrl.filename,
          });

          return {
            filename: presignedUrl.filename,
            url,
          };
        })
      );

      const reuslt = presignedUrlData.data.map((data) => {
        return {
          ...data,
          url:
            uploadFiles.find((file) => file.filename === data.filename)?.url ??
            "",
        };
      });

      return reuslt;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return [];
    }
  }

  $: if (submissionError) {
    // eslint-disable-next-line no-console
    console.error(submissionError);
  }

  async function onStateChange(args: OnFormStateChangeArgs) {
    submissionState = args.state;
  }

  let siteUrl = "";
  (async () => {
    const campaignListJson = await campaignListJsonState.get();
    siteUrl = campaignListJson.site.url ?? "";
    // eslint-disable-next-line no-console
  })().catch(console.log);
</script>

{#if page === "fail"}
  <div class="page-fail">
    <div>
      <img src={failImage} alt="fail" width="282" class="image" />
      <!-- <h2 class="title">설문조사 참여 불가!</h2> -->
      <h2 class="title">{failTitle}</h2>
      <!-- <p class="description">진행 중인 설문조사가 아닙니다.</p> -->
      {#if failType !== "not_targeted"}
        <p class="description">{failDescription}</p>
      {/if}
    </div>
  </div>
{:else if page === "main" && documentNode}
  <div class="wrapper" style={wrapperStyle}>
    <div>
      <EmbeddedApp
        document={documentNode}
        campaignSetting={{
          language: documentData?.language,
          siteUrl,
        }}
        translation={t}
        {dataMap}
        {onImpressionEvent}
        {onSendCollectEvent}
        {onUpload}
        {onSubmit}
        {onStateChange}
        captureMode={false}
      />
    </div>
  </div>
{:else if page === "success"}
  <div class="page-success">
    <div>
      <img src={successImage} alt="success" width="282" class="image" />
      <!-- <h2 class="title">제출 완료!</h2> -->
      <h2 class="title">{successTitle}</h2>
      <!-- <p class="description">설문조사에 참여해 주셔서 감사합니다.</p> -->
      <p class="description">{successDescription}</p>
    </div>
  </div>
{/if}

<Overlay show={loading} backdropStyle="white">
  <Loader color={"black"} />
  <span style="width: 7px"></span>
  <span>{$t("main.loading")}</span>
</Overlay>

<Overlay
  show={submissionState === "submitting" || submissionState === "uploading"}
>
  <Loader />
  <span style="width: 7px"></span>
  <span style="color: white">{$t("main.alreadySubmitting")}</span>
</Overlay>

<style>
  .wrapper {
    /* max-width: 700px; */
    width: 100%;
  }

  @media (min-width: 768px) {
    .wrapper {
      /* margin-top: 50px; */
      margin-top: var(--cb-wide-wrapper-margin-top);
    }
  }

  .page-success,
  .page-fail {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100vw;
    height: 100vh;
    text-align: center;
    padding-bottom: 20vh;
  }

  .page-success .image,
  .page-fail .image {
    border-radius: 15px;
    margin: 0 auto;
  }

  .page-success .title,
  .page-fail .title {
    font-size: 1.375em;
    font-weight: bold;
    margin-top: 50px;
  }

  .page-success .description,
  .page-fail .description {
    font-size: 1.125em;
    margin-top: 18px;
    font-weight: medium;
  }

  @keyframes zoom {
    0% {
      transform: scale(0.95);
    }
    100% {
      transform: scale(1);
    }
  }
</style>
